// services/PositionApiService.js
import {
    APIPositionList,
    APIPositionStore,
    APIPositionUpdate,
    APIPositionDelete,
    APIPositionNames
} from '../services/APIs';
import { api } from './authService';
import {
    connectionErrorMsg,
    Message_400,
    Message_401,
    Message_403,
    Message_404,
    Message_405,
    Message_409,
    Message_422,
    Message_500,
    Message_503
} from './errorMsgs';

class PositionApiService {
    constructor() {
        this.showToast = null;
    }

    /**
     * Set the toast function from useToast hook
     */
    setToastFunction(toastFunction) {
        this.showToast = toastFunction;
    }

    /**
     * Handle API errors consistently based on backend controller structure
     */
    handleError(error, defaultMessage = 'Operation failed') {
        let message = defaultMessage;
        let statusCode = null;
        let errors = null;
        let isIntegrityConstraint = false;

        if (error.response) {
            statusCode = error.response.status;
            const responseData = error.response.data || {};
            
            // Use backend message if available, otherwise fallback to our defaults
            message = responseData.message || defaultMessage;
            errors = responseData.errors || null;

            // Check for database integrity constraint errors
            const backendError = responseData.error || '';
            isIntegrityConstraint = backendError.includes('Integrity constraint violation') || 
                                   backendError.includes('foreign key constraint fails') ||
                                   backendError.includes('1451');

            // Handle specific backend response patterns
            switch (statusCode) {
                case 400:
                    // Handle business logic errors (like "Cannot delete position with employees")
                    if (responseData.message && responseData.message.includes('Cannot delete')) {
                        message = responseData.message;
                    } else {
                        message = message || Message_400;
                    }
                    break;
                case 401:
                    message = message || Message_401;
                    break;
                case 403:
                    message = message || Message_403;
                    break;
                case 404:
                    message = message || Message_404;
                    break;
                case 405:
                    message = message || Message_405;
                    break;
                case 409:
                    message = message || Message_409;
                    break;
                case 422:
                    // Backend returns validation errors with 'errors' object
                    if (errors) {
                        // Format validation errors into a readable string
                        const errorMessages = Object.values(errors)
                            .flat()
                            .map(err => `• ${err}`)
                            .join('\n');
                        message = `${message || Message_422}:\n${errorMessages}`;
                    } else {
                        message = message || Message_422;
                    }
                    break;
                case 500:
                    // Handle database integrity constraint errors
                    if (isIntegrityConstraint) {
                        message = 'Cannot delete this position because it has related employees assigned. ' +
                                 'Please reassign or remove the employees first before deleting this position.';
                    } 
                    // Handle other database errors
                    else if (backendError && backendError.includes('Database Error')) {
                        message = `Database Error: ${message}`;
                    } else {
                        message = message || Message_500;
                    }
                    break;
                case 503:
                    message = message || Message_503;
                    break;
                default:
                    message = message || `Server error (${statusCode})`;
            }
        } else if (error.request) {
            message = connectionErrorMsg;
        } else {
            message = error.message || defaultMessage;
        }

        if (this.showToast) {
            this.showToast("Error", message, "error");
        } else {
            console.warn('Toast function not set. Message:', message);
        }

        return {
            success: false,
            message,
            statusCode,
            errors,
            isIntegrityConstraint,
            error: error.response?.data || error
        };
    }

    /**
     * Get position names (for dropdowns)
     */
    async listNames(branchId = null) {
        try {
            const params = {};
            if (branchId) { params.branch_id = branchId; }
            const response = await api.get(APIPositionNames, { params });
            const responseData = response.data;
            
            // Check backend response status
            if (responseData.status === 'success') {
                return {
                    success: true,
                    data: responseData.data || [],
                    message: responseData.message || 'Positions fetched successfully'
                };
            } else {
                // Backend returned error status
                return {
                    success: false,
                    data: responseData.data || null,
                    message: responseData.message || 'Failed to fetch positions'
                };
            }
        } catch (error) {
            return this.handleError(error, 'Failed to fetch positions');
        }
    }

    /**
     * Get position list with full details (for table)
     */
    async list(branchId = null) {
        try {
            const params = {};
            if (branchId) { params.branch_id = branchId; }
            const response = await api.get(APIPositionList, { params });
            const responseData = response.data;
            
            // Check backend response status
            if (responseData.status === 'success') {
                const positions = Array.isArray(responseData.data) ? responseData.data : [];
                return {
                    success: true,
                    data: positions,
                    message: responseData.message || 'Positions fetched successfully'
                };
            } else {
                // Backend returned error status
                return {
                    success: false,
                    data: responseData.data || null,
                    message: responseData.message || 'Failed to fetch positions'
                };
            }
        } catch (error) {
            return this.handleError(error, 'Failed to fetch positions');
        }
    }

    /**
     * Create position
     */
    async create(positionData) {
        try {
            const response = await api.post(APIPositionStore, positionData);
            const responseData = response.data;
            
            // Check backend response status
            if (responseData.status === 'success') {
                return {
                    success: true, 
                    data: responseData.data,
                    message: responseData.message || 'Position created successfully'
                };
            } else {
                // Backend returned error status (validation or duplicate)
                if (response.status === 422 && responseData.errors) {
                    // Format validation errors for display
                    const errorMessages = Object.values(responseData.errors)
                        .flat()
                        .map(err => `• ${err}`)
                        .join('\n');
                    return {
                        success: false,
                        message: `${responseData.message || 'Validation failed'}:\n${errorMessages}`,
                        errors: responseData.errors
                    };
                }
                return {
                    success: false,
                    data: responseData.data,
                    message: responseData.message || 'Failed to create position'
                };
            }
        } catch (error) {
            return this.handleError(error, 'Failed to create position');
        }
    }

    /**
     * Update position
     */
    async update(positionData) {
        try {
            const response = await api.post(APIPositionUpdate, positionData);
            const responseData = response.data;
            
            if (responseData.status === 'success') {
                return {
                    success: true,
                    data: responseData.data,
                    message: responseData.message || 'Position updated successfully'
                };
            } else {
                // Handle different error cases
                if (response.status === 404) {
                    return {
                        success: false,
                        message: responseData.message || 'Position not found'
                    };
                } else if (response.status === 422) {
                    if (responseData.errors) {
                        const errorMessages = Object.values(responseData.errors)
                            .flat()
                            .map(err => `• ${err}`)
                            .join('\n');
                        return {
                            success: false,
                            data: responseData.data,
                            message: `${responseData.message || 'Validation failed'}:\n${errorMessages}`,
                            errors: responseData.errors
                        };
                    } else if (responseData.message === 'Given Record Data Already Exists') {
                        return {
                            success: false,
                            message: responseData.message,
                            errors: responseData.errors
                        };
                    }
                }
                return {
                    success: false,
                    message: responseData.message || 'Failed to update position'
                };
            }
        } catch (error) {
            return this.handleError(error, 'Failed to update position');
        }
    }

    /**
     * Delete position
     */
    async delete(positionData) {
        try {
            const response = await api.post(APIPositionDelete, positionData);
            const responseData = response.data;
            // Check backend response status
            if (responseData.status === 'success') {
                return {
                    success: true,
                    message: responseData.message || 'Position deleted successfully'
                };
            } else {
                // Handle business logic errors (400 status with custom message)
                if (response.status === 400 && responseData.message) {
                    return {
                        success: false,
                        message: responseData.message,
                        statusCode: 400
                    };
                }
                if (response.status === 404) {
                    return {
                        success: false,
                        message: responseData.message || 'Position not found'
                    };
                }
                return {
                    success: false,
                    message: responseData.message || 'Failed to delete position'
                };
            }
        } catch (error) {
            return this.handleError(error, 'Failed to delete position');
        }
    }
}

export const positionApiService = new PositionApiService();